import { getIdToken } from 'firebase/auth'
import { auth } from '@/frontend/lib/firebase'

// API Configuration
function getApiConfig(company: string) {
  const baseApiUrl = import.meta.env.VITE_DATASLOT_HTTPAPI_BASE_URL || 'https://dev.open-api.dataslot.app'
  
  return {
    module: 'ATMOper',
    company,
    baseUrl: `${baseApiUrl}/users/v1`
  }
}

export interface CreateInvitationPayload {
  newRole: 'Admin' | 'Member'
  provider: 'FIREBASE'
}

export interface CreateInvitationResponse {
  success: boolean
  data?: {
    invitationId: string
    invitationUrl: string
    expiresAt: string
    role: string
  }
  error?: string
}

export interface ApiInvitationResponse {
  resCode: string
  data: {
    inviteCode: string
    inviteRole: string
    inviteExpireTimestamp: number
    provider: string
    invitationUrl: string
    acceptEndpoint: string
    legacyUrls: {
      line: string
      firebase: string
    }
  }
}

export interface AcceptInvitationPayload {
  firebaseToken: string
}

export interface AcceptInvitationResponse {
  success: boolean
  data?: {
    userId: string
    role: string
    company: string
    module: string
  }
  error?: string
}

export interface Invitation {
  id: string
  role: 'Admin' | 'Member'
  status: 'pending' | 'accepted' | 'expired'
  createdAt: string
  expiresAt: string
  acceptedAt?: string
  invitationUrl: string
}

// User account types based on /accounts API response
export interface UserAccount {
  company: string
  gUId: string
  role: string
  lUId: string
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'
  sk: string
  employeeNumber: string
  module: string
  pk: string
  branchId: string
  lastActiveTimestamp: number
  registerTimestamp: number
  userInfo: {
    displayName: string
    pictureUrl?: string
  }
  teamId: string
  lastName: string
  firstName: string
  roleInfo: {
    roleEn: string
    roleTh: string
    rolePermission: Record<string, any> // Dynamic permissions structure
  }
}

export interface AccountsResponse {
  resCode: string
  data: UserAccount[]
}

// Company access types based on /companies API response
export interface CompanyAccess {
  role: string
  gUId: string
  company: string
  lUId: string
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'
  sk: string
  provider: string
  module: string
  pk: string
}

export interface CompaniesResponse {
  resCode: string
  data: CompanyAccess[]
}

/**
 * Create a new Firebase invitation
 */
export async function createInvitation(company: string, role: 'Admin' | 'Member' = 'Member'): Promise<CreateInvitationResponse> {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated')
    }

    const token = await getIdToken(auth.currentUser)
    const apiConfig = getApiConfig(company)
    const url = `${apiConfig.baseUrl}/${apiConfig.module}/${apiConfig.company}/invite`
    
    const payload: CreateInvitationPayload = {
      newRole: role,
      provider: 'FIREBASE'
    }

    console.log('[InvitationService] Creating invitation:', { url, payload })

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as any
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    const apiData = await response.json() as ApiInvitationResponse
    console.log('[InvitationService] Invitation created:', apiData)
    
    // Parse the API response into our expected format
    if (apiData.resCode === '200' && apiData.data) {
      const baseUrl = window.location.origin
      const fullInvitationUrl = `${baseUrl}${apiData.data.invitationUrl}`
      
      return {
        success: true,
        data: {
          invitationId: apiData.data.inviteCode,
          invitationUrl: fullInvitationUrl,
          expiresAt: new Date(apiData.data.inviteExpireTimestamp).toISOString(),
          role: apiData.data.inviteRole
        }
      }
    } else {
      return {
        success: false,
        error: 'Invalid response from server'
      }
    }
  } catch (error) {
    console.error('[InvitationService] Failed to create invitation:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create invitation'
    }
  }
}

/**
 * Accept a Firebase invitation
 */
export async function acceptInvitation(inviteCode: string, company: string, module: string = 'ATMOper'): Promise<AcceptInvitationResponse> {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated')
    }

    if (!inviteCode || !company) {
      throw new Error('Invalid invitation - missing required information')
    }

    const firebaseToken = await getIdToken(auth.currentUser)
    const apiConfig = getApiConfig(company)
    const url = `${apiConfig.baseUrl}/${module}/${company}/invite`
    
    const payload = {
      inviteCode: inviteCode
    }

    console.log('[InvitationService] Accepting invitation:', { url, inviteCode, company, module })

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${firebaseToken}`
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as any
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json() as any
    console.log('[InvitationService] Invitation accepted:', data)
    
    // Parse the response based on the actual API structure
    if (data.resCode === '200' || data.resCode === 200) {
      return {
        success: true,
        data: {
          userId: data.data?.userId || '',
          role: data.data?.role || '',
          company: company,
          module: module
        }
      }
    } else {
      return {
        success: false,
        error: data.message || 'Failed to accept invitation'
      }
    }
  } catch (error) {
    console.error('[InvitationService] Failed to accept invitation:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to accept invitation'
    }
  }
}

/**
 * Get list of invitations (if API supports it)
 * This is a placeholder for future implementation
 */
export async function getInvitations(company: string): Promise<{ success: boolean; data?: Invitation[]; error?: string }> {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated')
    }

    const token = await getIdToken(auth.currentUser)
    const apiConfig = getApiConfig(company)
    const url = `${apiConfig.baseUrl}/${apiConfig.module}/${apiConfig.company}/invitations`
    
    console.log('[InvitationService] Getting invitations:', { url })

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as any
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json() as any
    console.log('[InvitationService] Invitations retrieved:', data)
    
    return {
      success: true,
      data: data.invitations || []
    }
  } catch (error) {
    console.error('[InvitationService] Failed to get invitations:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get invitations'
    }
  }
}

/**
 * Get all user accounts
 */
export async function getUserAccounts(company: string): Promise<{ success: boolean; data?: UserAccount[]; error?: string }> {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated')
    }

    const token = await getIdToken(auth.currentUser)
    const apiConfig = getApiConfig(company)
    const url = `${apiConfig.baseUrl}/${apiConfig.module}/${apiConfig.company}/accounts`
    
    console.log('[InvitationService] Getting user accounts:', { url })

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as any
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json() as AccountsResponse
    console.log('[InvitationService] User accounts retrieved:', data)
    
    if (data.resCode === '200') {
      return {
        success: true,
        data: data.data
      }
    } else {
      throw new Error(`API error: ${data.resCode}`)
    }
  } catch (error) {
    console.error('[InvitationService] Failed to get user accounts:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get user accounts'
    }
  }
}

/**
 * Update user role
 */
export async function updateUserRole(userId: string, newRole: string, company: string): Promise<{ success: boolean; error?: string }> {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated')
    }

    const token = await getIdToken(auth.currentUser)
    const apiConfig = getApiConfig(company)
    const url = `${apiConfig.baseUrl}/${apiConfig.module}/${apiConfig.company}/accounts/${userId}/role`
    
    console.log('[InvitationService] Updating user role:', { url, userId, newRole })

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ role: newRole })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as any
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json() as any
    console.log('[InvitationService] User role updated:', data)
    
    return { success: true }
  } catch (error) {
    console.error('[InvitationService] Failed to update user role:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update user role'
    }
  }
}

/**
 * Update user status (activate/deactivate)
 */
export async function updateUserStatus(userId: string, status: 'ACTIVE' | 'INACTIVE', company: string): Promise<{ success: boolean; error?: string }> {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated')
    }

    const token = await getIdToken(auth.currentUser)
    const apiConfig = getApiConfig(company)
    const url = `${apiConfig.baseUrl}/${apiConfig.module}/${apiConfig.company}/accounts/${userId}/status`
    
    console.log('[InvitationService] Updating user status:', { url, userId, status })

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ status })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as any
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json() as any
    console.log('[InvitationService] User status updated:', data)
    
    return { success: true }
  } catch (error) {
    console.error('[InvitationService] Failed to update user status:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update user status'
    }
  }
}

/**
 * Get user's available companies
 */
export async function getCompanies(): Promise<{ success: boolean; data?: CompanyAccess[]; error?: string }> {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated')
    }

    const token = await getIdToken(auth.currentUser)
    // Use a temporary config to get base URL - companies endpoint doesn't need specific company
    const baseConfig = getApiConfig('temp') // We only need baseUrl and module
    const url = `${baseConfig.baseUrl}/${baseConfig.module}/companies`
    
    console.log('[InvitationService] Getting companies:', { url })

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as any
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json() as CompaniesResponse
    console.log('[InvitationService] Companies retrieved:', data)
    
    if (data.resCode === '200') {
      return {
        success: true,
        data: data.data
      }
    } else {
      throw new Error(`API error: ${data.resCode}`)
    }
  } catch (error) {
    console.error('[InvitationService] Failed to get companies:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get companies'
    }
  }
}

/**
 * Copy invitation URL to clipboard
 */
export async function copyInvitationLink(url: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(url)
      return true
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea')
      textArea.value = url
      textArea.style.position = 'absolute'
      textArea.style.left = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      const success = document.execCommand('copy')
      textArea.remove()
      return success
    }
  } catch (error) {
    console.error('[InvitationService] Failed to copy link:', error)
    return false
  }
}